import { Dispatch, SetStateAction, useCallback, useEffect } from "react";

import { DirtyPayload, useDirtyStateManager } from "utility/DirtyStateManager";
import { colorizeJson } from "utility/Utils";

import * as RD from "reports/ReportDef";

export enum AutoSaveStatus {
    Idle = 1,
    Pending = 2,
    InProgress = 3
}

interface AutoSaveReportPatcherParams {
    // Callbacks
    onAutoSaveDataGenerated: Dispatch<SetStateAction<RD.ReportGeneratedData>>;
    onAutoSaveStatusChanged: Dispatch<SetStateAction<AutoSaveStatus>>;
}

export const AutoSaveReportPatcher = ({
    // Callbacks
    onAutoSaveDataGenerated,
    onAutoSaveStatusChanged = null
}: AutoSaveReportPatcherParams) => {

    const dirtyManager = useDirtyStateManager();

    // ---=== Utilities ===--- //

    const logAutoSavePatchData = (reportPatchData: any) => {
        console.group(`📝 Report Patch Data`);
        console.log(colorizeJson(reportPatchData));
        console.groupEnd();
    };

    const generateAutoSaveReportPatchData = (autoSavePayload: DirtyPayload): RD.ReportGeneratedData => {
        const autoSaveReportPatchData = {};

        // Direct field mapping
        const directFields = [
            "assessmentsGenerated",
            "historyOfInjury",
            "discoveries",
            "engagementLevel",
            "goals",
            "goalsProgress",
            "subjectiveProgressSummary",
            "behavioralAssessments",
            "continuedNeedsAssessments",
            "physicalAssessments",
            "socialAssessments",
            "plan",
            "planSettings"
        ];

        // Add fields that exist in payload
        directFields.forEach(field => {
            if (field in autoSavePayload) {
                autoSaveReportPatchData[field] = autoSavePayload[field];
            }
        });

        // Inclusions mapping
        const inclusionFields = {
            selectedImprovedTrajectoryTypes: "selectedImprovedTrajectoryTypes",
            includeAverageTime: "averageTime",
            includeEngagementLevel: "engagementLevel",
            includeGoalsProgress: "goalsProgress",
            includeMedicationReduction: "medicationReduction",
            includeReturnToWork: "returnToWork",
            includeSessionsAttended: "sessionsAttended",
            includeUniqueDays: "uniqueDays",
            includedTrajectories: "trajectories",
            includeBehavioralAssessments: "behavioralAssessments",
            includeContinuedNeedsAssessments: "continuedNeedsAssessments",
            includePhysicalAssessments: "physicalAssessments",
            includeSocialAssessments: "socialAssessments"
        };

        // Build inclusions object if any inclusion fields exist
        const inclusions
            = Object.entries(inclusionFields)
                .reduce((acc, [payloadKey, inclusionKey]) => {
                    if (payloadKey in autoSavePayload) {
                        acc[inclusionKey] = autoSavePayload[payloadKey];
                    }
                    return acc;
                }, {});

        if (Object.keys(inclusions).length > 0) {
            autoSaveReportPatchData["inclusions"] = inclusions;
        }

        logAutoSavePatchData(autoSaveReportPatchData);

        return autoSaveReportPatchData;
    };

    const autoSaveHandler = useCallback(
        (dirtyPayload: DirtyPayload) => {
            console.warn("---=== AutoSave Callback Start ===---");
            const numDirtyEntries: number = Object.keys(dirtyPayload).length;
            if (numDirtyEntries > 0) {
                onAutoSaveStatusChanged(AutoSaveStatus.InProgress);
                console.warn(`AutoSave: Preparing ${numDirtyEntries} dirty ${numDirtyEntries === 1 ? "entry" : "entries"}`);
                onAutoSaveDataGenerated(generateAutoSaveReportPatchData(dirtyPayload));
            }
            else {
                // This should really never be called since the DirtyManager
                // should not invoke the autoSaveHandler function with an
                // empty payload.
                console.warn("AutoSave: No dirty entries found.");
                onAutoSaveStatusChanged(AutoSaveStatus.Idle);
            }
            console.warn("---=== AutoSave Callback End ===---");
        }, [dirtyManager]
    );

    // ---=== Component Mounting ===--- //

    useEffect(
        () => {
            console.log(`📖 MOUNTED AutoSaveReportPatcher`);
            return () => {
                console.log("📘 UNUNMOUNTED AutoSaveReportPatcher");
            };
        }, []
    );

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            dirtyManager.setDirtyCallbacks({
                onDirty: () => {
                    onAutoSaveStatusChanged(AutoSaveStatus.Pending);
                },
                onClean: () => {
                    onAutoSaveStatusChanged(AutoSaveStatus.Idle);
                }
            });

            dirtyManager.setAutoSaveConfig({
                intervalMs: 3000, // e.g., check every 3 seconds
                triggerCallback: autoSaveHandler
            });

            // Cleanup function when the component unmounts
            return () => {
                dirtyManager.setAutoSaveConfig({});
                dirtyManager.setDirtyCallbacks({});
            };
        },
        [dirtyManager, autoSaveHandler]
    );

    return null;
};